import React, { useState } from 'react'
import { useTar } from '@/modules/i18n'
import { CohortPersonDetails } from '../cohortPageData'
import { PartnerService, UserService, ChallengeService } from '@duck/api-definitions'
import { RelativeDateTime } from '../../datetime/RelativeDateTime'
import { cn, Card, CardContent } from '@duck/ui'
import { GradeBadge } from '../../GradeBadge/GradeBadge'
import { AssignmentSubmissionSummaryDialog } from './AssignmentSubmissionSummaryDialog'
import { useOrganizationSettings } from '@/hooks/useOrganizationSettings'

interface FeedbackDetails {
    personName: string
    feedback: string
    startDate?: string
    submitDate?: string
    grade?: number
    solution?: string
    workTime?: number
    charts?: ChallengeService.WorkState['charts']
}

interface AssignmentProgressTableProps {
    peopleDetails: CohortPersonDetails[]
    persons: UserService.Person[]
    sentTime: string
}

export const AssignmentProgressTable: React.FC<AssignmentProgressTableProps> = ({
    peopleDetails,
    persons,
    sentTime,
}) => {
    const { t } = useTar()
    const { getOrgSetting } = useOrganizationSettings()
    const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false)
    const [selectedFeedback, setSelectedFeedback] = useState<FeedbackDetails | null>(null)

    const invitationEmails = getOrgSetting('invitationEmails') ?? false

    const getPersonName = (personId: string): string => {
        const person = persons.find(p => p.id === personId)
        return person ? `${person.firstName || ''} ${person.lastName || ''}`.trim() : ''
    }

    const statusStepMap: Record<PartnerService.AssignmentDetailsStatus, number> = {
        [PartnerService.AssignmentDetailsStatus.Sent]: 1,
        [PartnerService.AssignmentDetailsStatus.Started]: 2,
        [PartnerService.AssignmentDetailsStatus.Submitted]: 3,
        [PartnerService.AssignmentDetailsStatus.Forfeited]: 0,
    }

    const getStatusStep = (status?: PartnerService.AssignmentDetailsStatus): number => {
        if (!status) return 0
        return statusStepMap[status] ?? 0
    }

    const shouldHighlightCell = (step: number, currentStep: number): boolean => {
        return step <= currentStep && currentStep > 0
    }

    const progressCellClassName = "bg-gradient-to-br from-primary/60 to-primary/40 border border-primary/30 rounded-md px-3 py-1.5 flex flex-col items-center justify-center min-h-[2rem] shadow-sm"

    const renderPersonRow = (personDetail: CohortPersonDetails, index: number) => {
        const currentStep = getStatusStep(personDetail.assignmentStatus)
        const personName = getPersonName(personDetail.personId)

        return (
            <div
                key={personDetail.personId}
                className={cn(
                    'grid grid-cols-4 border-b border-border last:border-b-0',
                    index % 2 === 0 ? 'bg-card' : 'bg-card/50'
                )}
            >
                {/* Name column */}
                <div className="px-4 py-2 text-sm text-foreground">
                    {personName || personDetail.email}
                </div>

                {/* Sent column */}
                <div className="px-2 py-2 text-sm text-center">
                    {shouldHighlightCell(1, currentStep) ? (
                        <div className={progressCellClassName}>
                            <RelativeDateTime
                                isoDateString={sentTime}
                                className="text-xs"
                            />
                        </div>
                    ) : null}
                </div>

                {/* Started column */}
                <div className="px-2 py-2 text-sm text-center">
                    {shouldHighlightCell(2, currentStep) ? (
                        <div className={progressCellClassName}>
                            {personDetail.startDate && (
                                <RelativeDateTime
                                    isoDateString={personDetail.startDate}
                                    className="text-xs"
                                />
                            )}
                        </div>
                    ) : null}
                </div>

                {/* Submitted column */}
                <div className="px-2 py-2 text-sm text-center flex flex-row items-center justify-center gap-2">
                    {shouldHighlightCell(3, currentStep) ? (
                        <>
                            <div className={cn(progressCellClassName, 'flex-1')}>
                                {personDetail.submitDate && (
                                    <RelativeDateTime
                                        isoDateString={personDetail.submitDate}
                                        className="text-xs"
                                    />
                                )}
                            </div>
                            {personDetail.feedback && personDetail.grade !== undefined ? (
                                <div className="flex justify-center items-center gap-2">
                                    <div
                                        onClick={() => {
                                            setSelectedFeedback({
                                                personName: personName || personDetail.email,
                                                feedback: personDetail.feedback || '',
                                                startDate: personDetail.startDate,
                                                submitDate: personDetail.submitDate,
                                                workTime: personDetail.workTime,
                                                grade: personDetail.grade,
                                                solution: personDetail.solution,
                                                charts: personDetail.charts,
                                            })
                                            setFeedbackDialogOpen(true)
                                        }}
                                        className="cursor-pointer"
                                    >
                                        <GradeBadge grade={personDetail.grade} small />
                                    </div>
                                </div>
                            ) : <div className="text-xs text-muted-foreground">{t('challenges.feedbackProcessing')}...</div>}
                        </>
                    ) : null}
                </div>
            </div>
        )
    }

    const renderPersonCard = (personDetail: CohortPersonDetails) => {
        const personName = getPersonName(personDetail.personId)

        return (
            <Card key={personDetail.personId} className="mb-4">
                <CardContent className="p-4 space-y-4">
                    <div className="flex flex-row items-center justify-between">
                        <div className="font-semibold text-foreground">
                            {personName || personDetail.email}
                        </div>
                        <div className="">
                            {personDetail.feedback && personDetail.grade !== undefined ? (
                                <div
                                    onClick={() => {
                                        setSelectedFeedback({
                                            personName: personName || personDetail.email,
                                            feedback: personDetail.feedback || '',
                                            startDate: personDetail.startDate,
                                            submitDate: personDetail.submitDate,
                                            workTime: personDetail.workTime,
                                            grade: personDetail.grade,
                                            solution: personDetail.solution,
                                            charts: personDetail.charts,
                                        })
                                        setFeedbackDialogOpen(true)
                                    }}
                                    className="cursor-pointer flex justify-start"
                                >
                                    <GradeBadge grade={personDetail.grade} small />
                                </div>
                            ) : personDetail.assignmentStatus === PartnerService.AssignmentDetailsStatus.Submitted ? <span className="text-xs text-muted-foreground">{t('challenges.feedbackProcessing')}...</span> : null}

                        </div>
                    </div>

                    {/* Sent */}
                    <div className="space-y-1">
                        <div className="text-xs font-medium text-muted-foreground">
                            {t('common.sent')}:{" "}
                            <RelativeDateTime
                                isoDateString={sentTime}
                                className="text-xs"
                            />
                        </div>
                    </div>

                    {/* Started */}
                    <div className="space-y-1">
                        <div className="text-xs font-medium text-muted-foreground">
                            {t('common.started')}:{" "}
                            {personDetail.startDate ? (
                                <RelativeDateTime
                                    isoDateString={personDetail.startDate}
                                    className="text-xs"
                                />
                            ) : <span className="text-xs text-muted-foreground">-</span>}
                        </div>
                    </div>

                    {/* Submitted */}
                    <div className="space-y-1">
                        <div className="text-xs font-medium text-muted-foreground">
                            {t('common.submitted')}:{" "}
                            {personDetail.submitDate ? (
                                <RelativeDateTime
                                    isoDateString={personDetail.submitDate}
                                    className="text-xs"
                                />
                            ) : <span className="text-xs text-muted-foreground">-</span>}
                        </div>
                    </div>
                </CardContent>
            </Card>
        )
    }

    return (
        <>
            {/* Desktop Table View */}
            <div className="hidden md:block">
                <div className="border border-border rounded-lg overflow-hidden">
                    <div className="grid grid-cols-4 border-b border-border bg-muted/10">
                        <div className="px-4 py-3 text-sm font-semibold text-foreground">
                            {t('common.name')}
                        </div>
                        <div className="px-4 py-3 text-sm font-semibold text-foreground text-center">
                            {invitationEmails ? t('common.sent') : t('common.created')}
                        </div>
                        <div className="px-4 py-3 text-sm font-semibold text-foreground text-center">
                            {t('common.started')}
                        </div>
                        <div className="px-4 py-3 text-sm font-semibold text-foreground text-center">
                            {t('common.submitted')}
                        </div>
                    </div>
                    <div>
                        {peopleDetails.map((personDetail, index) => renderPersonRow(personDetail, index))}
                    </div>
                </div>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden">
                {peopleDetails.map((personDetail) => renderPersonCard(personDetail))}
            </div>

            <AssignmentSubmissionSummaryDialog
                open={feedbackDialogOpen}
                onOpenChange={setFeedbackDialogOpen}
                feedbackDetails={selectedFeedback}
            />
        </>
    )
}

